/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useState } from 'react';
import { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { closeDialog, openDialog } from '../../../../app/dialog/dialog.actions';
import { createCompanyContext } from '../../../../app/page/company-context/company-context.actions';
import { deleteCompanyToolsCategory } from '../../../../app/page/page-storage/company-tools-categories/company-tools-categories.actions';
import { authSelectors } from '../../../../app/auth/auth.selectors';

import { companyToolsCategorySelectors } from '../../../../app/resource/company-tools-category/company-tools-category.selectors';

import { useSelect, useSelectWithParams } from '../../../../utils/selector.utils';
import { IAgencyDescriptionDialogProps } from '../../../dialogs/AgencyDescriptionDialog/types';
import { ICompanyToolsCategoryFormDialogProps } from '../../../dialogs/CompanyToolsCategoryFormDialog/types';
import { CircularAvatar } from '../../common/CustomAvatar';
import { CompanyContextItem } from './CompanyContextItem';
import { ICompanyToolsCategoryProps } from './types';

export const CompanyToolsCategory: FunctionComponent<ICompanyToolsCategoryProps> = ({ companyToolsCategoryId }) => {
  const companyToolsCategory = useSelectWithParams(
    companyToolsCategorySelectors.selectResourceById,
    companyToolsCategoryId,
  );
  const isAdmin = useSelect(authSelectors.selectUserIsAdmin);
  const title = companyToolsCategory?.title;
  const description = companyToolsCategory?.description;
  const dispatch = useDispatch();
  const [isToggledPanel, setIsToggledPanel] = useState(true);
  const handleTogglePanel = useCallback(() => setIsToggledPanel((prev) => !prev), []);
  const closePanel = useCallback(() => setIsToggledPanel(true), []);

  const openAgencyDescriptionDialog = useCallback(() => {
    dispatch(openDialog('agencyDescriptionDialog', { title, description } as IAgencyDescriptionDialogProps));
  }, [description, dispatch, title]);

  const openUpdateDialog = useCallback(() => {
    if (!companyToolsCategory) {
      return;
    }
    dispatch(
      openDialog('companyToolsCategoryFormDialog', {
        data: companyToolsCategory,
      } as ICompanyToolsCategoryFormDialogProps),
    );
    closePanel();
  }, [companyToolsCategory, closePanel, dispatch]);

  const onAddSection = useCallback(() => {
    dispatch(createCompanyContext(companyToolsCategoryId));
  }, [companyToolsCategoryId, dispatch]);

  const onDelete = useCallback(() => {
    dispatch(
      openDialog('deleteDialog', {
        onConfirm: () => {
          dispatch(closeDialog('deleteDialog'));
          dispatch(deleteCompanyToolsCategory(companyToolsCategoryId));
        },
        title: 'Delete category',
        message: 'Are you sure you want to delete this category?',
      }),
    );
    closePanel();
  }, [companyToolsCategoryId, closePanel, dispatch]);

  return (
    <div className="panel agency-tools-item">
      <div className="agency-item-top-part">
        <div className="agency-tool-actions">
          <span
            className="agency-tool-info link popup_button"
            data-popup="tooltip_popup"
            data-tooltip_text={description}
            data-tooltip_title={title}
          >
            <i className="icon-info blueGray" onClick={openAgencyDescriptionDialog} />
          </span>
          {isAdmin && (
            <div className={`settings-button f-18 dropdown ${!isToggledPanel ? 'opened' : ''}`}>
              <span style={{ cursor: 'pointer' }} onClick={handleTogglePanel}>
                <i className="icon-settings blueGray" />
              </span>
              <ul className="dropdown-menu settings-menu drop-right" onMouseLeave={closePanel}>
                <li>
                  <a style={{ cursor: 'pointer' }} className="drop-item" onClick={openUpdateDialog}>
                    Update
                  </a>
                </li>
                <li>
                  <a style={{ cursor: 'pointer' }} className="drop-item" onClick={onDelete}>
                    Delete
                  </a>
                </li>
              </ul>
            </div>
          )}
        </div>
        <div className="agency-icon">
          <CircularAvatar
            src={companyToolsCategory?.avatar ?? undefined}
            title={companyToolsCategory?.title ?? ''}
            color={companyToolsCategory?.color}
            size="50"
            cursorPointer={false}
            styles={{ marginRight: 0 }}
          />
        </div>
        <h2 className="bold mt-2">{companyToolsCategory?.title}</h2>
      </div>
      <div className="agency-item-bottom-part pt-0 pb-3 px-5">
        <div className="list w100 d-block showAllContainer">
          {(companyToolsCategory?.companyContextIds ?? []).map((companyContextId) => (
            <CompanyContextItem
              key={companyContextId}
              companyContextId={companyContextId}
              color={companyToolsCategory?.color ?? ''}
            />
          ))}
        </div>
        {isAdmin && companyToolsCategory && (
          <div className="w100 flex aic jcc mt-2">
            <a
              className="btn btn-md"
              title="Add Section"
              style={{
                backgroundColor: companyToolsCategory.color,
                color: '#fff',
                borderColor: companyToolsCategory.color,
              }}
              onClick={onAddSection}
            >
              <i className="icon-plus mr-1"></i> Add Section
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
